import React, { useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { groupSizeOptions, locationOptions, planningOptions, type LocalizedOption } from '../data/quoteOptions';
import { emptyQuoteForm, type QuoteFormData } from '../types/quote';
const TOTAL_STEPS = 6;
function OptionGrid({
  options,
  value,
  onSelect,
  columns = 'sm:grid-cols-2'
}: {
  options: LocalizedOption[];
  value: string;
  onSelect: (option: string) => void;
  columns?: string;
}) {
  const {
    language
  } = useLanguage();
  return <div className={`grid grid-cols-1 gap-3 ${columns}`} data-id="element-112" data-mp="31y3vgselmgn0">
      {options.map(option => {
      const isSelected = value === option.value;
      return <button key={option.value} type="button" onClick={() => onSelect(option.value)} aria-pressed={isSelected} className={`flex items-center justify-between border px-5 py-4 text-left text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gold ${isSelected ? 'border-gold bg-gold/10 text-ivory' : 'border-ivory/20 text-ivory/70 hover:border-ivory/50 hover:text-ivory'}`} data-id="element-113" data-mp="3bv8mb5je30a5">
            {option.label[language]}
            {isSelected ? <CheckIcon size={16} className="text-gold" data-id="element-114" data-mp="3hq6vvebr2a3" /> : null}
          </button>;
    })}
    </div>;
}
export function QuoteForm() {
  const {
    language,
    t
  } = useLanguage();
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [form, setForm] = useState<QuoteFormData>(() => ({
    ...emptyQuoteForm,
    preferredLanguage: language === 'es' ? 'Español' : 'English'
  }));
  const [submitted, setSubmitted] = useState(false);
  function update<K extends keyof QuoteFormData>(key: K, value: QuoteFormData[K]) {
    setForm(current => ({
      ...current,
      [key]: value
    }));
  }
  function isStepValid(current: number): boolean {
    switch (current) {
      case 1:
        return Boolean(form.planningType);
      case 2:
        return form.noDateYet || Boolean(form.eventDate);
      case 3:
        return Boolean(form.location);
      case 4:
        return Boolean(form.groupSize);
      case 5:
        return true;
      case 6:
        return form.name.trim().length > 1 && /\S+@\S+\.\S+/.test(form.email);
      default:
        return false;
    }
  }
  function goTo(next: number) {
    setDirection(next > step ? 1 : -1);
    setStep(next);
  }
  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!isStepValid(6)) return;
    setSubmitted(true);
  }
  const variants = {
    enter: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? 24 : -24
    }),
    center: {
      opacity: 1,
      x: 0
    },
    exit: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? -24 : 24
    })
  };
  return <section id="quote" className="bg-ink px-5 py-24 text-ivory md:px-8 md:py-32 lg:px-12" data-id="element-115" data-mp="1gnlfnw3xo93x">
      <div className="mx-auto max-w-3xl" data-id="element-116" data-mp="3sax9mvcve14t">
        <div className="text-center" data-id="element-117" data-mp="132jcj487ezk">
          <p className="text-xs font-semibold uppercase tracking-widest2 text-gold" data-id="element-118" data-mp="121amlaon4l0">{t.quote.kicker}</p>
          <h2 className="mt-5 font-display text-5xl leading-[0.95] md:text-6xl" data-id="element-119" data-mp="3jga664qrnyv7">
            {t.quote.headingOne}<br data-id="element-120" data-mp="3ngax2ov1y2j5" /><span className="italic text-gold-soft" data-id="element-121" data-mp="3ngat6drxlidv">{t.quote.headingTwo}</span>
          </h2>
          <p className="mx-auto mt-6 max-w-md text-base text-ivory/68" data-id="element-122" data-mp="39h1ejijdiwc0">{t.quote.description}</p>
        </div>

        <div className="mt-14 border border-ivory/12 bg-charcoal/60 p-6 md:p-10" data-id="element-123" data-mp="3efcr4111zkxx">
          {!submitted ? <>
              <div className="mb-10 flex items-center justify-between" data-id="element-124" data-mp="34izx121lj52n">
                <span className="text-[11px] font-semibold uppercase tracking-widest2 text-ivory/45" data-id="element-125" data-mp="34lcele3fzdf6">
                  {t.quote.step} {step} {t.quote.of} {TOTAL_STEPS} — {t.quote.labels[step - 1]}
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-widest2 text-gold" data-id="element-126" data-mp="e18ur25zdfwa">{Math.round(step / TOTAL_STEPS * 100)}%</span>
              </div>
              <div className="mb-10 h-[2px] w-full bg-ivory/12" data-id="element-127" data-mp="92xgmf8nkj89">
                <motion.div className="h-full bg-gold" animate={{
              width: `${step / TOTAL_STEPS * 100}%`
            }} transition={{
              duration: 0.3,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-128" data-mp="421f28brx960" />
              </div>

              <form onSubmit={handleSubmit} data-id="element-129" data-mp="3mh28xacuafa6">
                <AnimatePresence mode="wait" custom={direction} initial={false} data-id="element-130" data-mp="3mg0v4y4fveor">
                  <motion.div key={step} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.24,
                ease: [0.25, 1, 0.5, 1]
              }} className="min-h-[280px]" data-id="element-131" data-mp="3hh6458wbijtk">
                    {step === 1 ? <div data-id="element-132" data-mp="1izdv0xlsjagx">
                        <h3 className="mb-6 font-display text-2xl" data-id="element-133" data-mp="1j28fgp4t0vac">{t.quote.lookingFor}</h3>
                        <OptionGrid options={planningOptions} value={form.planningType ?? ''} onSelect={value => update('planningType', value as QuoteFormData['planningType'])} data-id="element-134" data-mp="1n3twmmodfvqc" />
                      </div> : null}

                    {step === 2 ? <div data-id="element-135" data-mp="zdhdefnoeckg">
                        <h3 className="mb-6 font-display text-2xl" data-id="element-136" data-mp="za3fbjm1opit">{t.quote.when}</h3>
                        <label htmlFor="event-date" className="mb-2 block text-xs uppercase tracking-widest2 text-ivory/50" data-id="element-137" data-mp="14biwv1p0kdwg">{t.quote.dateLabel}</label>
                        <input id="event-date" type="date" value={form.eventDate} disabled={form.noDateYet} onChange={event => update('eventDate', event.target.value)} className="w-full border border-ivory/25 bg-transparent px-4 py-3.5 text-ivory disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-gold [color-scheme:dark]" data-id="element-138" data-mp="1e1kpm6sbakhp" />
                        <label className="mt-5 flex items-center gap-3 text-sm text-ivory/70" data-id="element-139" data-mp="n8ty06h5ku0s">
                          <input type="checkbox" checked={form.noDateYet} onChange={event => update('noDateYet', event.target.checked)} className="h-4 w-4 accent-burgundy" data-id="element-140" data-mp="ibtspxs4nvaq" />
                          {t.quote.noDate}
                        </label>
                      </div> : null}

                    {step === 3 ? <div data-id="element-141" data-mp="1fy14psqrgpx4">
                        <h3 className="mb-6 font-display text-2xl" data-id="element-142" data-mp="1fx98qgt94q6b">{t.quote.where}</h3>
                        <OptionGrid options={locationOptions} value={form.location} onSelect={value => update('location', value)} data-id="element-143" data-mp="1ayejox4oy5dr" />
                      </div> : null}

                    {step === 4 ? <div data-id="element-144" data-mp="2xxh1p50ijk0a">
                        <h3 className="mb-6 font-display text-2xl" data-id="element-145" data-mp="2jwecwki4vvlo">{t.quote.people}</h3>
                        <OptionGrid options={groupSizeOptions} value={form.groupSize} onSelect={value => update('groupSize', value)} columns="sm:grid-cols-3" data-id="element-146" data-mp="2jyr1vhmb7688" />
                      </div> : null}

                    {step === 5 ? <div data-id="element-147" data-mp="3gldkryet2vdc">
                        <h3 className="mb-6 font-display text-2xl" data-id="element-148" data-mp="3gorob9z7hamd">{t.quote.vision}</h3>
                        <textarea value={form.vision} onChange={event => update('vision', event.target.value)} placeholder={t.quote.visionPlaceholder} rows={6} className="w-full border border-ivory/25 bg-transparent px-4 py-3.5 text-ivory placeholder:text-ivory/35 focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-149" data-mp="32uqvewuxqhtx" />
                      </div> : null}

                    {step === 6 ? <div data-id="element-150" data-mp="1qhtw6diz6vfe">
                        <h3 className="mb-6 font-display text-2xl" data-id="element-151" data-mp="1qj4uvyxkdczn">{t.quote.contact}</h3>
                        <div className="grid gap-4 sm:grid-cols-2" data-id="element-152" data-mp="1lktl1o79r2hf">
                          <div className="sm:col-span-2" data-id="element-153" data-mp="2j7sn6egqd2mp">
                            <label htmlFor="quote-name" className="mb-2 block text-xs uppercase tracking-widest2 text-ivory/50" data-id="element-154" data-mp="2j56eucm0mj3f">{t.quote.name}</label>
                            <input id="quote-name" type="text" required value={form.name} onChange={event => update('name', event.target.value)} className="w-full border border-ivory/25 bg-transparent px-4 py-3.5 text-ivory focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-155" data-mp="298bjg2l1gytd" />
                          </div>
                          <div data-id="element-156" data-mp="tr9n4r5hhegw">
                            <label htmlFor="quote-email" className="mb-2 block text-xs uppercase tracking-widest2 text-ivory/50" data-id="element-157" data-mp="tuffcf9eswgu">{t.quote.email}</label>
                            <input id="quote-email" type="email" required value={form.email} onChange={event => update('email', event.target.value)} className="w-full border border-ivory/25 bg-transparent px-4 py-3.5 text-ivory focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-158" data-mp="39gi0l504rbwx" />
                          </div>
                          <div data-id="element-159" data-mp="3o54fj5lui1nm">
                            <label htmlFor="quote-phone" className="mb-2 block text-xs uppercase tracking-widest2 text-ivory/50" data-id="element-160" data-mp="3o8a32getsvh8">{t.quote.phone}</label>
                            <input id="quote-phone" type="tel" value={form.phone} onChange={event => update('phone', event.target.value)} className="w-full border border-ivory/25 bg-transparent px-4 py-3.5 text-ivory focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-161" data-mp="1180g2fswgrs" />
                          </div>
                        </div>

                        <div className="mt-6" data-id="element-162" data-mp="27sftwoe2n25q">
                          <span className="mb-2 block text-xs uppercase tracking-widest2 text-ivory/50" data-id="element-163" data-mp="3ou1tasbelgf9">{t.quote.preferredLanguage}</span>
                          <div className="flex gap-3" data-id="element-164" data-mp="3tto4kw7o8mlf">
                            {(['English', 'Español'] as const).map(preferredLanguage => <button key={preferredLanguage} type="button" onClick={() => update('preferredLanguage', preferredLanguage)} aria-pressed={form.preferredLanguage === preferredLanguage} className={`border px-5 py-2.5 text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gold ${form.preferredLanguage === preferredLanguage ? 'border-gold bg-gold/10 text-ivory' : 'border-ivory/20 text-ivory/60 hover:border-ivory/50'}`} data-id="element-165" data-mp="7lolxz70v4nx">
                                {preferredLanguage === 'English' ? t.quote.english : t.quote.spanish}
                              </button>)}
                          </div>
                        </div>
                      </div> : null}
                  </motion.div>
                </AnimatePresence>

                <div className="mt-10 flex items-center justify-between border-t border-ivory/12 pt-8" data-id="element-166" data-mp="25x85j2yhm5w0">
                  {step > 1 ? <button type="button" onClick={() => goTo(step - 1)} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest2 text-ivory/60 transition-colors duration-200 hover:text-ivory focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-167" data-mp="2yj9j8ywcifrc">
                      <ArrowLeftIcon size={14} data-id="element-168" data-mp="3mb6t4kg8ixpt" /> {t.quote.back}
                    </button> : <span data-id="element-169" data-mp="1fld7b9dhrgvv" />}

                  {step < TOTAL_STEPS ? <button type="button" disabled={!isStepValid(step)} onClick={() => goTo(step + 1)} className="inline-flex items-center gap-2 bg-burgundy px-7 py-3.5 text-xs font-semibold uppercase tracking-widest2 text-ivory transition-colors duration-200 hover:bg-burgundy-dark disabled:cursor-not-allowed disabled:opacity-30" data-id="element-170" data-mp="1jmoz9lju8e78">
                      {t.quote.continue} <ArrowRightIcon size={14} data-id="element-171" data-mp="10xs4lgkj3joh" />
                    </button> : <button type="submit" disabled={!isStepValid(6)} className="inline-flex items-center gap-2 bg-burgundy px-7 py-3.5 text-xs font-semibold uppercase tracking-widest2 text-ivory transition-colors duration-200 hover:bg-burgundy-dark disabled:cursor-not-allowed disabled:opacity-30" data-id="element-172" data-mp="my0nh6yptn69">
                      {t.quote.submit}
                    </button>}
                </div>
              </form>
            </> : <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} className="flex min-h-[280px] flex-col items-center justify-center text-center" data-id="element-173" data-mp="355k22bk5rc2k">
              <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold text-gold" data-id="element-174" data-mp="3a5ersbs75vm1">
                <CheckIcon size={22} data-id="element-175" data-mp="3o3mvd2jqnv66" />
              </span>
              <h3 className="mt-6 font-display text-3xl md:text-4xl" data-id="element-176" data-mp="3k32zs3etczm5">
                {t.quote.thanks}<br data-id="element-177" data-mp="3k0yov1ihz0cd" /><span className="italic text-gold-soft" data-id="element-178" data-mp="3k18e2mvpx3h0">{t.quote.success}</span>
              </h3>
              <p className="mt-5 max-w-sm text-sm text-ivory/65" data-id="element-179" data-mp="1tsggy977cszi">{t.quote.followUp}</p>
            </motion.div>}
        </div>
      </div>
    </section>;
}